// Rails パラメータ変換用のヘルパー関数集

/**
 * ステップ1: 開発プロセス（スクラムの周期対応）の属性を変換する
 * @param {Array<string|Object>} developmentMethods - 画面で選ばれた開発プロセス配列（文字列 or {id,name,...}）
 * @param {Array<Object>|null} existingItems - 既存のレコード（編集時のみ）
 * @param {boolean} isEditMode - 編集モードかどうか
 * @param {number|string|undefined} globalSprintCycleWeek - フォーム全体の sprint_cycle_week
 * @returns {Object} Rails nested_attributes の attributes オブジェクト
 */
export const transformDevelopmentMethodsAttributes = (
  developmentMethods,
  existingItems,
  isEditMode,
  globalSprintCycleWeek
) => {
  const attributes = {};
  let attributeIndex = 0;

  const current = developmentMethods || []; // フォームで選ばれている開発プロセス一覧
  const existing = isEditMode ? existingItems || [] : []; // 既存データ（編集時のみ）

  // id または name からキーを作る
  const keyOf = (item) => {
    const id =
      item?.development_method_id || item?.id || item?.development_method?.id;
    const name =
      item?.development_method_name ||
      item?.name ||
      item?.development_method?.name ||
      "";
    return id ? `id:${id}` : `name:${name}`;
  };

  // 既存データをマップに変換（削除判定に使う）
  const existingMap = new Map();
  existing.forEach((old) => existingMap.set(keyOf(old), old));

  // フォームで選ばれている項目を追加・更新として処理
  current.forEach((dm) => {
    const name = typeof dm === "string" ? dm : dm?.name || "";
    const id =
      typeof dm === "string" ? "" : dm?.id || dm?.development_method_id || "";
    const key = keyOf(dm);
    const matched = existingMap.get(key);

    attributes[attributeIndex] = {
      id: matched?.id || "",
      development_method_id: id,
      development_method_name: name,
      sprint_cycle_week:
        name === "スクラム" && globalSprintCycleWeek
          ? Number(globalSprintCycleWeek)
          : undefined,
      _destroy: "false", // 選択中のものは削除しない
    };

    existingMap.delete(key); // 処理済みとして削除
    attributeIndex++;
  });

  // フォームから消えた既存データは削除対象
  existingMap.forEach((old) => {
    attributes[attributeIndex] = { id: old.id, _destroy: "true" };
    attributeIndex++;
  });

  return attributes;
};

/**
 * ステップ2-7の操作系の属性を変換する
 * @param {Array} operations - 操作データ配列
 * @param {Array} existingOperations - 既存の操作データ
 * @param {string} idField - IDフィールド名
 * @param {boolean} isEditMode - 編集モードかどうか
 * @returns {Object} 変換された属性オブジェクト
 */
export const transformOperationsAttributes = (
  operations,
  existingOperations,
  idField,
  isEditMode
) => {
  const attributes = {};
  let attributeIndex = 0;

  if (isEditMode && existingOperations) {
    // 既存の操作を処理（削除または更新）
    existingOperations.forEach((existingOperation) => {
      const currentOperation = operations?.find(
        (current) =>
          current.operation_id === existingOperation[idField] &&
          current.content === existingOperation.content
      );

      attributes[attributeIndex.toString()] = {
        id: existingOperation.id,
        [idField]: existingOperation[idField] || "",
        content: existingOperation.content || "",
        _destroy: currentOperation ? "false" : "true",
      };
      attributeIndex++;
    });

    // 新しく追加された操作を処理
    if (operations && operations.length > 0) {
      operations.forEach((operation) => {
        if (
          (operation.operation_id && operation.operation_id !== "") ||
          (operation.content && operation.content !== "")
        ) {
          const isNewOperation = !existingOperations.some(
            (existing) =>
              existing[idField] === operation.operation_id &&
              existing.content === operation.content
          );

          if (isNewOperation) {
            attributes[attributeIndex.toString()] = {
              [idField]: operation.operation_id || "",
              content: operation.content || "",
              _destroy: "false",
            };
            attributeIndex++;
          }
        }
      });
    }
  } else {
    // 新規作成の場合
    if (operations && operations.length > 0) {
      operations.forEach((operation, index) => {
        if (
          (operation.operation_id && operation.operation_id !== "") ||
          (operation.content && operation.content !== "")
        ) {
          attributes[index.toString()] = {
            [idField]: operation.operation_id || "",
            content: operation.content || "",
            _destroy: "false",
          };
        }
      });
    }
  }

  return attributes;
};

/**
 * ステップ8-9の言語レコードを正規化する
 * @param {Array} languages - 言語データ配列
 * @param {string} languageType - 言語タイプ
 * @returns {Array} 正規化されたレコード配列
 */
export const normalizeLanguageRecords = (languages, languageType) => {
  const records = [];
  if (languages && languages.length > 0) {
    languages.forEach((language) => {
      if (
        (language.language_name && language.language_name.trim() !== "") ||
        (language.fw_library_name && language.fw_library_name.trim() !== "")
      ) {
        if (
          language.fw_library_name &&
          language.fw_library_name.trim() !== ""
        ) {
          const frameworks = language.fw_library_name
            .split(",")
            .map((s) => s.trim())
            .filter((s) => s !== "");
          frameworks.forEach((frameworkName) => {
            records.push({
              language_name: language.language_name || "",
              language_id: language.language_id,
              fw_library_name: frameworkName,
              language_type: languageType,
            });
          });
        } else {
          records.push({
            language_name: language.language_name || "",
            language_id: language.language_id,
            fw_library_name: "",
            language_type: languageType,
          });
        }
      }
    });
  }
  return records;
};

/**
 * ステップ8-9の言語・フレームワークの属性を変換する
 * @param {Array} frontendLanguages - フロントエンド言語配列
 * @param {Array} backendLanguages - バックエンド言語配列
 * @param {Array} existingLanguages - 既存の言語データ
 * @param {boolean} isEditMode - 編集モードかどうか
 * @returns {Object} 変換された属性オブジェクト
 */
export const transformLanguagesAttributes = (
  frontendLanguages,
  backendLanguages,
  existingLanguages,
  isEditMode
) => {
  const languagesAttributes = {};
  let attributeIndex = 0;

  // 現在のフロントエンド言語データを正規化
  const currentFrontendRecords = normalizeLanguageRecords(
    frontendLanguages,
    "front_end"
  );
  const currentBackendRecords = normalizeLanguageRecords(
    backendLanguages,
    "back_end"
  );
  const allCurrentRecords = [
    ...currentFrontendRecords,
    ...currentBackendRecords,
  ];

  if (isEditMode && existingLanguages) {
    // 既存の言語を処理（削除または更新）
    existingLanguages.forEach((existingLanguage) => {
      const currentRecord = allCurrentRecords.find(
        (current) =>
          current.language_name === existingLanguage.language_name &&
          current.fw_library_name === existingLanguage.fw_library_name &&
          current.language_type === existingLanguage.language_type
      );

      languagesAttributes[attributeIndex.toString()] = {
        id: existingLanguage.id,
        language_name: existingLanguage.language_name || "",
        language_id: existingLanguage.language_id,
        fw_library_name: existingLanguage.fw_library_name || "",
        fw_library_id: existingLanguage.fw_library_id || "",
        language_type: existingLanguage.language_type,
        _destroy: currentRecord ? "false" : "true",
      };
      attributeIndex++;
    });

    // 新しく追加された言語を処理
    allCurrentRecords.forEach((currentRecord) => {
      const isNewRecord = !existingLanguages.some(
        (existing) =>
          existing.language_name === currentRecord.language_name &&
          existing.fw_library_name === currentRecord.fw_library_name &&
          existing.language_type === currentRecord.language_type
      );

      if (isNewRecord) {
        languagesAttributes[attributeIndex.toString()] = {
          language_name: currentRecord.language_name,
          language_id: currentRecord.language_id,
          fw_library_name: currentRecord.fw_library_name,
          fw_library_id: "",
          language_type: currentRecord.language_type,
          _destroy: "false",
        };
        attributeIndex++;
      }
    });
  } else {
    // 新規作成の場合
    allCurrentRecords.forEach((record, index) => {
      languagesAttributes[index.toString()] = {
        language_name: record.language_name,
        language_id: record.language_id,
        fw_library_name: record.fw_library_name,
        fw_library_id: "",
        language_type: record.language_type,
        _destroy: "false",
      };
    });
  }

  return languagesAttributes;
};

/**
 * ステップ13-18のシンプルな属性を変換する（OS、ミドルウェア、DB等）
 * @param {Array} items - アイテム配列
 * @param {Array} existingItems - 既存のアイテム
 * @param {string} fieldPrefix - フィールドプレフィックス
 * @param {boolean} isEditMode - 編集モードかどうか
 * @returns {Object} 変換された属性オブジェクト
 */
export const transformSimpleAttributes = (
  items,
  existingItems,
  fieldPrefix,
  isEditMode
) => {
  const attributes = {};
  let attributeIndex = 0;

  const nameField = `${fieldPrefix}_name`;
  const idField = `${fieldPrefix}_id`;

  if (isEditMode && existingItems) {
    // 現在選択されているアイテム名を取得
    const currentItemNames = (items || [])
      .map((item) => item[nameField])
      .filter((name) => name && name.trim() !== "");

    // 既存のアイテムを処理
    existingItems.forEach((existingItem) => {
      const isSelected = currentItemNames.includes(existingItem[nameField]);

      attributes[attributeIndex.toString()] = {
        id: existingItem.id,
        [idField]: existingItem[idField] || "",
        [nameField]: existingItem[nameField],
        _destroy: isSelected ? "false" : "true",
      };
      attributeIndex++;
    });

    // 新しく追加されたアイテムを処理
    if (items && items.length > 0) {
      items.forEach((item) => {
        if (item[nameField] && item[nameField].trim() !== "") {
          const isNewItem = !existingItems.some(
            (existing) => existing[nameField] === item[nameField]
          );

          if (isNewItem) {
            attributes[attributeIndex.toString()] = {
              [idField]: item[idField] || "",
              [nameField]: item[nameField],
              _destroy: "false",
            };
            attributeIndex++;
          }
        }
      });
    }
  } else {
    // 新規作成の場合
    if (items && items.length > 0) {
      items.forEach((item, index) => {
        if (item[nameField] && item[nameField].trim() !== "") {
          attributes[index.toString()] = {
            [idField]: item[idField] || "",
            [nameField]: item[nameField],
            _destroy: "false",
          };
        }
      });
    }
  }

  return attributes;
};
